import React from 'react';
import { BaseModal } from './BaseModal';
import { ImageZoom } from './ImageZoom';

interface WebformInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WebformInfoModal({ isOpen, onClose }: WebformInfoModalProps) {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="OneTrust Hosted Webform"
      size="large"
    >
      <div className="space-y-8 text-gray-600 dark:text-gray-300">
        {/* Introduction */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-xl mb-8">
          <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-100 mb-4">
            Simple Integration, Powerful Results
          </h3>
          <p className="text-blue-800 dark:text-blue-200">
            Collect consent, feedback, or user data—no dev hours needed. With OneTrust-hosted web forms, you can build, launch, and embed forms in minutes, all from a single platform.
          </p>
        </div>

        {/* Section 1: Creation & Configuration */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">1. Creation & Configuration</h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <p className="mb-4">
              Design forms that fit your needs using OneTrust's intuitive, no-code builder. Customize fields, labels, data types, and required inputs—all from a clean, centralized dashboard.
            </p>
            <ImageZoom 
              src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/OT%20HOSTED%20WEBFORM/OT%20Webform%20Builder.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9PVCBIT1NURUQgV0VCRk9STS9PVCBXZWJmb3JtIEJ1aWxkZXIucG5nIiwiaWF0IjoxNzQ1ODU0Nzk4LCJleHAiOjIwMjk2Nzg3OTh9.ZNbCGciyEyVDLqQZPmsMMvzPU36RPRHQ6_O7MBUE2o4"
              alt="OneTrust Form Builder Interface"
              className="w-full h-auto object-contain max-h-[400px] mx-auto"
            />
          </div>
        </div>

        {/* Section 2: Publishing & Integration */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">2. Publishing & Integration</h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-6">
            <div>
              <p className="mb-4">
                When you're ready, hit publish—that's it. Share the form via direct link or get the script to embed it on your site.
              </p>
              <ImageZoom 
                src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/OT%20HOSTED%20WEBFORM/OT%20Webform%20script%20link.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9PVCBIT1NURUQgV0VCRk9STS9PVCBXZWJmb3JtIHNjcmlwdCBsaW5rLnBuZyIsImlhdCI6MTc0NTg1NDg2MiwiZXhwIjoyMDI5Njc4ODYyfQ.zmPwp-QdB6JomX1PwyLxNi0EmwxwntsPvvBDHQNfc2E"
                alt="OneTrust Form Share Link"
                className="w-full h-auto object-contain max-h-[300px] mx-auto"
              />
            </div>
            
            <div>
              <p className="mb-4">
                Or embed the form directly on your site with a simple iframe snippet:
              </p>
              <ImageZoom 
                src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/OT%20HOSTED%20WEBFORM/OT%20Webform%20script%20iframe.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9PVCBIT1NURUQgV0VCRk9STS9PVCBXZWJmb3JtIHNjcmlwdCBpZnJhbWUucG5nIiwiaWF0IjoxNzQ1ODU0ODk0LCJleHAiOjIwMjk2Nzg4OTR9.QCYRyiAVDUrJrfdU66kY9EJ9CiajZy8_aOHTtoQhaxg"
                alt="OneTrust Form Iframe Code"
                className="w-full h-auto object-contain max-h-[300px] mx-auto"
              />
            </div>
          </div>
        </div>

        {/* Section 3: Receipt Generation */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">3. Receipt Generation</h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <p className="mb-4">
              Each form submission automatically generates a detailed receipt in OneTrust, capturing all relevant data and consent information for compliance purposes.
            </p>
            <ImageZoom 
              src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/OT%20HOSTED%20WEBFORM/OT%20Webform%20Receipt.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9PVCBIT1NURUQgV0VCRk9STS9PVCBXZWJmb3JtIFJlY2VpcHQucG5nIiwiaWF0IjoxNzQ1ODU0OTY5LCJleHAiOjIwMjk2Nzg5Njl9.68ie0pqce9iJYPYMihK5Wwm98b3tbAjw8XmXiAx4M9g"
              alt="OneTrust Form Receipt"
              className="w-full h-auto object-contain max-h-[400px] mx-auto"
            />
          </div>
        </div>

        {/* Conclusion */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 p-6 rounded-xl">
          <p className="text-blue-800 dark:text-blue-200 mb-4">
            OneTrust-hosted webforms make it easy to gather the data you need while respecting privacy and boosting trust. Whether it's data access requests, contact forms, or consent capture—this is your all-in-one solution to scale privacy operations with confidence.
          </p>
          <p className="text-blue-800 dark:text-blue-200 font-semibold">
            Ready to go live in minutes? Start building smarter forms today.
          </p>
        </div>
      </div>
    </BaseModal>
  );
}